// ============================================================
// DRAVONIX ULTIMATE - SERVICES DATA (14 Services)
// Images mapped to /assets/services/1.png through 14.png
// ============================================================

export const services = [
  // ========== BLOCKCHAIN (9) ==========
  {
    id: 'blockchain',
    category: 'blockchain',
    icon: 'fa-cubes',
    title: 'Own Blockchain',
    tagline: 'Custom Infrastructure',
    badge: 'Enterprise Grade',
    badgeIcon: 'fa-shield-alt',
    color: 'cyan',
    image: '/assets/services/1.png',
    description: 'Build your own blockchain network from scratch with custom consensus mechanisms, governance models, and enterprise-grade security.',
    longDescription: 'Launch your own Layer-1 or Layer-2 blockchain tailored to your specific use case. Our expert team designs custom consensus mechanisms, implements advanced governance models, and ensures enterprise-grade security. From private permissioned networks to public decentralized chains, we build scalable infrastructure that powers the next generation of Web3 applications.',
    features: [
      { icon: 'fa-cogs', title: 'Custom Consensus', text: 'PoS, PoA, DPoS, or hybrid mechanisms tailored to your needs' },
      { icon: 'fa-tachometer-alt', title: '50,000+ TPS', text: 'High-performance architecture for massive scalability' },
      { icon: 'fa-link', title: 'Interoperability', text: 'Cross-chain bridges and IBC protocol integration' },
      { icon: 'fa-lock', title: 'Enterprise Security', text: 'Military-grade encryption and audit compliance' },
      { icon: 'fa-users', title: 'Governance', text: 'On-chain voting and proposal systems' },
      { icon: 'fa-code', title: 'Smart Contracts', text: 'Native smart contract support with custom VM' }
    ],
    stats: [
      { value: '50K+', label: 'TPS' },
      { value: '99.9%', label: 'Uptime' },
      { value: '<2s', label: 'Finality' },
      { value: '100+', label: 'Validators' }
    ],
    tech: ['Substrate', 'Cosmos SDK', 'Rust', 'Go', 'Tendermint'],
    process: ['Requirements Analysis', 'Architecture Design', 'Core Development', 'Testing & Audit', 'Mainnet Launch']
  },
  {
    id: 'dex-wallet',
    category: 'blockchain',
    icon: 'fa-wallet',
    title: 'DEX Wallet',
    tagline: 'Self-Custody Solutions',
    badge: 'Multi-Chain',
    badgeIcon: 'fa-layer-group',
    color: 'purple',
    image: '/assets/services/2.png',
    description: 'Non-custodial wallets with multi-chain support, biometric security, DeFi integrations, and seamless user experience.',
    longDescription: 'Create secure, user-friendly cryptocurrency wallets that put users in control of their assets. Our wallets feature military-grade encryption, biometric authentication, and seamless integration with DeFi protocols across 100+ blockchain networks. From browser extensions to mobile apps, we build wallets that balance security with exceptional UX.',
    features: [
      { icon: 'fa-link', title: '100+ Networks', text: 'Support for all major blockchains and Layer-2s' },
      { icon: 'fa-fingerprint', title: 'Biometric Auth', text: 'Face ID, Touch ID, and hardware key support' },
      { icon: 'fa-exchange-alt', title: 'Built-in DEX', text: 'Swap tokens directly within the wallet' },
      { icon: 'fa-image', title: 'NFT Gallery', text: 'Beautiful NFT display and management' },
      { icon: 'fa-bell', title: 'Price Alerts', text: 'Real-time notifications and portfolio tracking' },
      { icon: 'fa-shield-alt', title: 'Security', text: 'Hardware wallet integration and MPC support' }
    ],
    stats: [
      { value: '100+', label: 'Chains' },
      { value: '4.9★', label: 'Rating' },
      { value: '2M+', label: 'Users' },
      { value: '$500M+', label: 'Secured' }
    ],
    tech: ['Web3.js', 'Ethers.js', 'React Native', 'Swift', 'Kotlin'],
    process: ['UX Research', 'Security Architecture', 'Development', 'Security Audit', 'App Store Launch']
  },
  {
    id: 'dex-swap',
    category: 'blockchain',
    icon: 'fa-exchange-alt',
    title: 'DEX Swap',
    tagline: 'Decentralized Trading',
    badge: 'AMM Protocol',
    badgeIcon: 'fa-chart-line',
    color: 'emerald',
    image: '/assets/services/3.png',
    description: 'High-performance decentralized exchanges with AMM, concentrated liquidity, yield farming, and cross-chain swaps.',
    longDescription: 'Build next-generation decentralized exchanges that rival centralized platforms in speed and UX while maintaining full decentralization. Our DEX solutions feature advanced AMM algorithms, concentrated liquidity for capital efficiency, limit orders, and seamless cross-chain trading capabilities.',
    features: [
      { icon: 'fa-water', title: 'AMM Pools', text: 'Constant product, stable swap, and weighted pools' },
      { icon: 'fa-seedling', title: 'Yield Farming', text: 'Liquidity mining and staking rewards' },
      { icon: 'fa-random', title: 'Cross-chain', text: 'Swap assets across different blockchains' },
      { icon: 'fa-chart-pie', title: 'Analytics', text: 'Real-time charts, TVL tracking, and insights' },
      { icon: 'fa-sliders-h', title: 'Limit Orders', text: 'Advanced order types beyond market swaps' },
      { icon: 'fa-bolt', title: 'MEV Protection', text: 'Frontrunning and sandwich attack protection' }
    ],
    stats: [
      { value: '$5M+', label: 'Daily Vol' },
      { value: '0.01%', label: 'Slippage' },
      { value: '500+', label: 'Pairs' },
      { value: '$50M+', label: 'TVL' }
    ],
    tech: ['Solidity', 'Uniswap V3', 'TheGraph', 'Hardhat', 'IPFS'],
    process: ['Protocol Design', 'Smart Contracts', 'Frontend', 'Audit', 'Liquidity Bootstrap']
  },
  {
    id: 'hardware-wallet',
    category: 'blockchain',
    icon: 'fa-microchip',
    title: 'Hardware Wallet',
    tagline: 'Cold Storage',
    badge: 'Military Grade',
    badgeIcon: 'fa-shield-alt',
    color: 'gold',
    image: '/assets/services/4.png',
    description: 'Custom hardware wallets with secure elements, air-gapped signing, tamper-proof designs, and multi-signature support.',
    longDescription: 'Design and manufacture custom hardware wallets with the highest security standards. Our solutions feature certified secure elements, air-gapped transaction signing, tamper-evident enclosures, and support for multi-signature schemes. Perfect for institutions and high-net-worth individuals.',
    features: [
      { icon: 'fa-microchip', title: 'Secure Element', text: 'EAL5+ certified tamper-resistant chips' },
      { icon: 'fa-wifi', title: 'Air-gapped', text: 'QR code or NFC signing, no USB required' },
      { icon: 'fa-users', title: 'Multi-sig', text: 'M-of-N signature schemes for teams' },
      { icon: 'fa-ban', title: 'Tamper-proof', text: 'Physical security seals and detection' },
      { icon: 'fa-key', title: 'Backup', text: 'Shamir secret sharing and seed phrases' },
      { icon: 'fa-mobile-alt', title: 'Companion App', text: 'iOS and Android management apps' }
    ],
    stats: [
      { value: '100%', label: 'Offline' },
      { value: 'EAL5+', label: 'Certified' },
      { value: '50+', label: 'Coins' },
      { value: '0', label: 'Breaches' }
    ],
    tech: ['ARM TrustZone', 'C/C++', 'RTOS', 'Cryptography', 'HSM'],
    process: ['Hardware Design', 'Firmware Development', 'Security Certification', 'Manufacturing', 'Distribution']
  },
  {
    id: 'smart-contracts',
    category: 'blockchain',
    icon: 'fa-file-contract',
    title: 'Smart Contracts',
    tagline: 'Audited & Optimized',
    badge: 'Security First',
    badgeIcon: 'fa-check-circle',
    color: 'cyan',
    image: '/assets/services/5.png',
    description: 'Bulletproof smart contracts with formal verification, gas optimization, and comprehensive security audits.',
    longDescription: 'Deploy production-ready smart contracts that are secure, gas-efficient, and battle-tested. Our development process includes formal verification, multiple audit rounds, and continuous monitoring. We specialize in DeFi protocols, NFT systems, DAOs, and complex multi-contract architectures.',
    features: [
      { icon: 'fa-search', title: 'Security Audits', text: 'Multiple rounds with top audit firms' },
      { icon: 'fa-gas-pump', title: '40% Gas Savings', text: 'Optimized bytecode and storage patterns' },
      { icon: 'fa-arrow-up', title: 'Upgradeable', text: 'Proxy patterns for future improvements' },
      { icon: 'fa-code-branch', title: 'Multi-chain', text: 'Deploy to any EVM-compatible chain' },
      { icon: 'fa-bug', title: 'Bug Bounty', text: 'Ongoing security incentive programs' },
      { icon: 'fa-file-alt', title: 'Documentation', text: 'Complete NatSpec and integration guides' }
    ],
    stats: [
      { value: '$2B+', label: 'TVL Secured' },
      { value: '40%', label: 'Gas Saved' },
      { value: '0', label: 'Exploits' },
      { value: '150+', label: 'Audits' }
    ],
    tech: ['Solidity', 'Vyper', 'Foundry', 'Slither', 'Echidna'],
    process: ['Requirements', 'Architecture', 'Development', 'Testing', 'Audit & Deploy']
  },
  {
    id: 'nft-marketplace',
    category: 'blockchain',
    icon: 'fa-store',
    title: 'NFT Marketplace',
    tagline: 'Digital Collectibles',
    badge: 'Multi-Chain',
    badgeIcon: 'fa-gem',
    color: 'purple',
    image: '/assets/services/6.png',
    description: 'Full-featured NFT marketplaces with lazy minting, royalties, auctions, and cross-chain NFT bridges.',
    longDescription: 'Launch your own NFT marketplace with all the features users expect and more. Our platforms support lazy minting, creator royalties, various auction types, collections, and advanced discovery features. We also build cross-chain NFT bridges to expand your marketplace reach.',
    features: [
      { icon: 'fa-magic', title: 'Lazy Minting', text: 'Mint NFTs without upfront gas costs' },
      { icon: 'fa-percentage', title: 'Royalties', text: 'Automatic creator royalty enforcement' },
      { icon: 'fa-gavel', title: 'Auctions', text: 'English, Dutch, and reserve auctions' },
      { icon: 'fa-random', title: 'Cross-chain', text: 'Bridge NFTs between networks' },
      { icon: 'fa-search', title: 'Discovery', text: 'AI-powered recommendations and search' },
      { icon: 'fa-wallet', title: 'Multi-wallet', text: 'Support for all major wallets' }
    ],
    stats: [
      { value: '$100M+', label: 'Volume' },
      { value: '500K+', label: 'NFTs' },
      { value: '50K+', label: 'Creators' },
      { value: '10+', label: 'Chains' }
    ],
    tech: ['ERC-721', 'ERC-1155', 'IPFS', 'Arweave', 'TheGraph'],
    process: ['Market Research', 'Smart Contracts', 'Platform Dev', 'Testing', 'Launch']
  },
  {
    id: 'token-launchpad',
    category: 'blockchain',
    icon: 'fa-rocket',
    title: 'Token Launchpad',
    tagline: 'Fair Token Sales',
    badge: 'IDO Platform',
    badgeIcon: 'fa-rocket',
    color: 'emerald',
    image: '/assets/services/7.png',
    description: 'Decentralized launchpad platforms with fair launch mechanisms, vesting schedules, and KYC integration.',
    longDescription: 'Build a professional token launchpad that enables projects to raise funds fairly and transparently. Our platforms include multiple sale types, anti-bot measures, tiered allocation systems, vesting contracts, and optional KYC integration for compliant launches.',
    features: [
      { icon: 'fa-balance-scale', title: 'Fair Launch', text: 'FCFS, lottery, and tiered systems' },
      { icon: 'fa-robot', title: 'Anti-bot', text: 'Sophisticated bot detection and prevention' },
      { icon: 'fa-clock', title: 'Vesting', text: 'Customizable token release schedules' },
      { icon: 'fa-id-card', title: 'KYC/AML', text: 'Optional compliance integration' },
      { icon: 'fa-layer-group', title: 'Tiers', text: 'Staking-based allocation tiers' },
      { icon: 'fa-shield-alt', title: 'Insurance', text: 'Refund mechanisms for failed projects' }
    ],
    stats: [
      { value: '$50M+', label: 'Raised' },
      { value: '100+', label: 'Projects' },
      { value: '25x', label: 'Avg ROI' },
      { value: '0', label: 'Rugs' }
    ],
    tech: ['Solidity', 'Chainlink', 'React', 'Node.js', 'PostgreSQL'],
    process: ['Platform Design', 'Contract Dev', 'Frontend', 'Security Audit', 'Launch']
  },
  {
    id: 'defi-protocol',
    category: 'blockchain',
    icon: 'fa-landmark',
    title: 'DeFi Protocol',
    tagline: 'Financial Infrastructure',
    badge: 'Yield Optimization',
    badgeIcon: 'fa-chart-line',
    color: 'gold',
    image: '/assets/services/8.png',
    description: 'Custom DeFi protocols including lending platforms, yield aggregators, and synthetic asset systems.',
    longDescription: 'Design and build sophisticated DeFi protocols that push the boundaries of decentralized finance. We create lending/borrowing platforms, yield aggregators, synthetic assets, perpetual exchanges, and novel financial primitives with robust security and capital efficiency.',
    features: [
      { icon: 'fa-hand-holding-usd', title: 'Lending', text: 'Variable and fixed-rate lending pools' },
      { icon: 'fa-chart-line', title: 'Yield Farming', text: 'Auto-compounding strategies' },
      { icon: 'fa-coins', title: 'Synthetics', text: 'Create synthetic assets and derivatives' },
      { icon: 'fa-infinity', title: 'Perpetuals', text: 'Leveraged perpetual trading' },
      { icon: 'fa-shield-alt', title: 'Risk Management', text: 'Liquidation engines and oracles' },
      { icon: 'fa-code-branch', title: 'Composability', text: 'Integration with existing protocols' }
    ],
    stats: [
      { value: '$500M+', label: 'TVL' },
      { value: '15%', label: 'Avg APY' },
      { value: '0', label: 'Hacks' },
      { value: '50+', label: 'Integrations' }
    ],
    tech: ['Solidity', 'Chainlink', 'Aave', 'Compound', 'Yearn'],
    process: ['Economic Design', 'Smart Contracts', 'Simulations', 'Audit', 'Mainnet']
  },
  {
    id: 'crypto-exchange',
    category: 'blockchain',
    icon: 'fa-chart-bar',
    title: 'Crypto Exchange',
    tagline: 'Trading Platform',
    badge: 'CEX Solution',
    badgeIcon: 'fa-exchange-alt',
    color: 'orange',
    image: '/assets/services/9.png',
    description: 'Full-featured centralized exchange platforms with matching engines, custody solutions, and compliance tools.',
    longDescription: 'Build a professional cryptocurrency exchange with institutional-grade infrastructure. Our exchange solutions include high-performance matching engines, secure custody systems, fiat on/off ramps, KYC/AML compliance, and comprehensive admin tools for managing the platform.',
    features: [
      { icon: 'fa-bolt', title: 'Matching Engine', text: 'Ultra-low latency order matching' },
      { icon: 'fa-vault', title: 'Custody', text: 'Hot/cold wallet infrastructure' },
      { icon: 'fa-university', title: 'Fiat Gateway', text: 'Bank integration and payment processing' },
      { icon: 'fa-id-badge', title: 'Compliance', text: 'Full KYC/AML/CFT compliance suite' },
      { icon: 'fa-chart-candlestick', title: 'Trading', text: 'Spot, margin, and futures trading' },
      { icon: 'fa-mobile-alt', title: 'Apps', text: 'Web, iOS, and Android applications' }
    ],
    stats: [
      { value: '$1B+', label: 'Daily Vol' },
      { value: '<1ms', label: 'Latency' },
      { value: '99.99%', label: 'Uptime' },
      { value: '1M+', label: 'Users' }
    ],
    tech: ['Rust', 'PostgreSQL', 'Redis', 'Kubernetes', 'AWS'],
    process: ['Architecture', 'Core Development', 'Security', 'Compliance', 'Launch']
  },
  {
    id: 'token-development',
    category: 'blockchain',
    icon: 'fa-coins',
    title: 'Crypto Token Development',
    tagline: 'Multi-Chain Tokens',
    badge: 'ERC-20 / BEP-20',
    badgeIcon: 'fa-gem',
    color: 'purple',
    image: '/assets/services/10.png',
    description: 'Custom token development across multiple chains with burnable, mintable, pausable, and advanced tokenomics features.',
    longDescription: 'Launch your own cryptocurrency token on any blockchain. We develop custom tokens with advanced features including burn mechanisms, minting capabilities, reflection rewards, anti-whale protection, and complex tokenomics. Deploy on Ethereum, BSC, Polygon, Solana, and 20+ other chains.',
    features: [
      { icon: 'fa-fire', title: 'Burnable', text: 'Deflationary mechanics with auto-burn' },
      { icon: 'fa-plus-circle', title: 'Mintable', text: 'Controlled supply expansion' },
      { icon: 'fa-pause-circle', title: 'Pausable', text: 'Emergency pause functionality' },
      { icon: 'fa-percentage', title: 'Reflection', text: 'Auto rewards to holders' },
      { icon: 'fa-whale', title: 'Anti-Whale', text: 'Max transaction/wallet limits' },
      { icon: 'fa-lock', title: 'Vesting', text: 'Time-locked token releases' }
    ],
    stats: [
      { value: '500+', label: 'Tokens' },
      { value: '20+', label: 'Chains' },
      { value: '100%', label: 'Audited' },
      { value: '$50M+', label: 'Market Cap' }
    ],
    tech: ['Solidity', 'Rust', 'OpenZeppelin', 'Hardhat', 'Foundry'],
    process: ['Tokenomics Design', 'Smart Contract', 'Audit', 'Deployment', 'Verification']
  },
  {
    id: 'blockchain-gaming',
    category: 'blockchain',
    icon: 'fa-gamepad',
    title: 'Blockchain Gaming',
    tagline: 'Play-to-Earn Games',
    badge: 'GameFi',
    badgeIcon: 'fa-trophy',
    color: 'emerald',
    image: '/assets/services/11.png',
    description: 'Full-stack blockchain game development with NFT integration, play-to-earn mechanics, and immersive gameplay.',
    longDescription: 'Create engaging blockchain games that players love. We build complete GameFi ecosystems with NFT characters, in-game economies, play-to-earn rewards, guild systems, and tournaments. From casual mobile games to complex MMORPGs, we deliver games that drive engagement and revenue.',
    features: [
      { icon: 'fa-trophy', title: 'Play-to-Earn', text: 'Reward players with tokens/NFTs' },
      { icon: 'fa-users', title: 'Guild System', text: 'Social features and scholarships' },
      { icon: 'fa-store', title: 'Marketplace', text: 'In-game NFT trading' },
      { icon: 'fa-coins', title: 'Game Economy', text: 'Balanced tokenomics design' },
      { icon: 'fa-mobile-alt', title: 'Cross-Platform', text: 'Web, mobile, and PC' },
      { icon: 'fa-infinity', title: 'Metaverse Ready', text: 'Interoperable assets' }
    ],
    stats: [
      { value: '50K+', label: 'DAU' },
      { value: '$10M+', label: 'Volume' },
      { value: '4.7★', label: 'Rating' },
      { value: '15+', label: 'Games' }
    ],
    tech: ['Unity', 'Unreal', 'Solidity', 'Node.js', 'WebGL'],
    process: ['Game Design', 'Economy Design', 'Development', 'Testing', 'Launch']
  },
  {
    id: 'metaverse-development',
    category: 'blockchain',
    icon: 'fa-vr-cardboard',
    title: 'Metaverse Platform',
    tagline: 'Virtual Worlds',
    badge: 'Web3 Metaverse',
    badgeIcon: 'fa-globe',
    color: 'cyan',
    image: '/assets/services/12.png',
    description: 'Build immersive metaverse experiences with virtual land, avatars, social features, and blockchain integration.',
    longDescription: 'Create your own metaverse platform or build experiences within existing ones. We develop virtual worlds with customizable avatars, virtual real estate, social hubs, events, commerce, and seamless blockchain integration for true digital ownership.',
    features: [
      { icon: 'fa-map', title: 'Virtual Land', text: 'NFT-based land ownership' },
      { icon: 'fa-user-astronaut', title: 'Avatars', text: 'Customizable 3D characters' },
      { icon: 'fa-comments', title: 'Social', text: 'Voice chat, events, gatherings' },
      { icon: 'fa-store', title: 'Commerce', text: 'Virtual shops and marketplaces' },
      { icon: 'fa-building', title: 'Building', text: 'User-generated content tools' },
      { icon: 'fa-vr-cardboard', title: 'VR Support', text: 'Oculus, Vive, WebXR' }
    ],
    stats: [
      { value: '100K+', label: 'Users' },
      { value: '10K+', label: 'Land Plots' },
      { value: '$25M+', label: 'Economy' },
      { value: '5+', label: 'Worlds' }
    ],
    tech: ['Three.js', 'Unity', 'WebXR', 'IPFS', 'Solidity'],
    process: ['World Design', '3D Development', 'Blockchain', 'Testing', 'Launch']
  },
  {
    id: 'crypto-apis',
    category: 'blockchain',
    icon: 'fa-plug',
    title: 'Cryptocurrency APIs',
    tagline: 'Blockchain Infrastructure',
    badge: 'Developer Tools',
    badgeIcon: 'fa-code',
    color: 'gold',
    image: '/assets/services/13.png',
    description: 'Build robust cryptocurrency APIs for wallets, exchanges, payments, and blockchain data with high availability.',
    longDescription: 'Power your applications with reliable cryptocurrency APIs. We build and deploy scalable API infrastructure for wallet operations, transaction broadcasting, blockchain data indexing, price feeds, and payment processing. Enterprise-grade reliability with 99.99% uptime.',
    features: [
      { icon: 'fa-wallet', title: 'Wallet APIs', text: 'Create, manage, sign transactions' },
      { icon: 'fa-broadcast-tower', title: 'Node APIs', text: 'Direct blockchain access' },
      { icon: 'fa-database', title: 'Data APIs', text: 'Indexed blockchain data' },
      { icon: 'fa-credit-card', title: 'Payment APIs', text: 'Accept crypto payments' },
      { icon: 'fa-chart-line', title: 'Price APIs', text: 'Real-time market data' },
      { icon: 'fa-bell', title: 'Webhooks', text: 'Event notifications' }
    ],
    stats: [
      { value: '1B+', label: 'Requests' },
      { value: '99.99%', label: 'Uptime' },
      { value: '<50ms', label: 'Latency' },
      { value: '50+', label: 'Chains' }
    ],
    tech: ['Node.js', 'Go', 'PostgreSQL', 'Redis', 'Kafka'],
    process: ['API Design', 'Development', 'Documentation', 'Testing', 'Deployment']
  },

  // ========== DEVELOPMENT (2) ==========
  {
    id: 'web-dev',
    category: 'development',
    icon: 'fa-globe',
    title: 'Web Development',
    tagline: 'Modern Web Apps',
    badge: 'Full Stack',
    badgeIcon: 'fa-laptop-code',
    color: 'cyan',
    image: '/assets/services/14.png',
    description: 'Build blazing-fast web applications with modern frameworks and exceptional user experiences.',
    longDescription: 'Create stunning, high-performance web applications that engage users and drive conversions. We use cutting-edge frameworks like Next.js and React to build fast, SEO-friendly websites with beautiful animations and seamless interactions. From landing pages to complex web apps.',
    features: [
      { icon: 'fa-bolt', title: 'Performance', text: 'Core Web Vitals optimized, 90+ Lighthouse' },
      { icon: 'fa-paint-brush', title: 'Design', text: 'Custom UI/UX with Figma integration' },
      { icon: 'fa-mobile-alt', title: 'Responsive', text: 'Perfect on every device and screen' },
      { icon: 'fa-plug', title: 'Integrations', text: 'APIs, CMS, and third-party services' },
      { icon: 'fa-search', title: 'SEO Ready', text: 'Built for search engine visibility' },
      { icon: 'fa-sync', title: 'Maintainable', text: 'Clean code and CMS integration' }
    ],
    stats: [
      { value: '98+', label: 'PageSpeed' },
      { value: '127%', label: 'Conversion Boost' },
      { value: '99.9%', label: 'Uptime' },
      { value: '50+', label: 'Sites Built' }
    ],
    tech: ['Next.js', 'React', 'TypeScript', 'Tailwind CSS', 'Vercel'],
    process: ['Discovery', 'Design', 'Development', 'Testing', 'Launch']
  },
  {
    id: 'mobile-dev',
    category: 'development',
    icon: 'fa-mobile-alt',
    title: 'Mobile App Development',
    tagline: 'Native & Cross-Platform',
    badge: 'iOS & Android',
    badgeIcon: 'fa-mobile-alt',
    color: 'purple',
    image: '/assets/services/15.png',
    description: 'Create powerful mobile experiences that engage users and drive business growth.',
    longDescription: 'Launch your mobile app on iOS and Android with a single codebase or native development. We build apps that users love, with smooth animations, offline support, push notifications, and seamless backend integration. From MVPs to enterprise apps.',
    features: [
      { icon: 'fa-users', title: 'Cross-platform', text: 'React Native or Flutter for faster delivery' },
      { icon: 'fa-sync', title: 'Real-time', text: 'Live updates and synchronization' },
      { icon: 'fa-lock', title: 'Security', text: 'Encryption and secure authentication' },
      { icon: 'fa-chart-bar', title: 'Analytics', text: 'User behavior tracking and insights' },
      { icon: 'fa-bell', title: 'Push Notifications', text: 'Engagement and retention features' },
      { icon: 'fa-cloud', title: 'Cloud Backend', text: 'Scalable serverless architecture' }
    ],
    stats: [
      { value: '4.8★', label: 'Avg Rating' },
      { value: '85%', label: 'Retention' },
      { value: '2x', label: 'Faster Dev' },
      { value: '30+', label: 'Apps Built' }
    ],
    tech: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase'],
    process: ['Planning', 'UI/UX Design', 'Development', 'Testing', 'App Store']
  },

  // ========== MARKETING (3) ==========
  {
    id: 'digital-marketing',
    category: 'marketing',
    icon: 'fa-chart-line',
    title: 'Digital Marketing',
    tagline: 'Growth & Engagement',
    badge: 'Data-Driven',
    badgeIcon: 'fa-bullseye',
    color: 'emerald',
    image: '/assets/services/16.png',
    description: 'Amplify your brand with data-driven strategies that drive traffic and maximize ROI.',
    longDescription: 'Grow your Web3 project with targeted digital marketing campaigns. We specialize in crypto-native marketing including influencer partnerships, community building, paid ads, SEO, and content marketing. Data-driven strategies that deliver measurable results.',
    features: [
      { icon: 'fa-ad', title: 'Paid Ads', text: 'Google, Twitter, and crypto ad networks' },
      { icon: 'fa-chart-bar', title: 'Analytics', text: 'Conversion tracking and attribution' },
      { icon: 'fa-users', title: 'Influencers', text: 'KOL partnerships and campaigns' },
      { icon: 'fa-search', title: 'SEO', text: 'Organic traffic and visibility' },
      { icon: 'fa-pen', title: 'Content', text: 'Blog posts, threads, and thought leadership' },
      { icon: 'fa-envelope', title: 'Email', text: 'Nurture sequences and newsletters' }
    ],
    stats: [
      { value: '450%', label: 'Avg ROI' },
      { value: '300%', label: 'Traffic Growth' },
      { value: '10M+', label: 'Impressions' },
      { value: '50+', label: 'Campaigns' }
    ],
    tech: ['Google Ads', 'Facebook Ads', 'Analytics', 'SEO Tools', 'CRM'],
    process: ['Audit', 'Strategy', 'Execution', 'Optimization', 'Reporting']
  },
  {
    id: 'graphic-design',
    category: 'marketing',
    icon: 'fa-palette',
    title: 'Graphic Designing',
    tagline: 'Visual Identity & Branding',
    badge: 'Creative Studio',
    badgeIcon: 'fa-paint-brush',
    color: 'orange',
    image: '/assets/services/17.png',
    description: 'Create stunning visual identities that capture your brand essence.',
    longDescription: 'Stand out in the crowded crypto space with distinctive visual branding. Our design team creates logos, brand guidelines, marketing materials, social media assets, and UI designs that communicate trust and innovation. From startups to established protocols.',
    features: [
      { icon: 'fa-lightbulb', title: 'Brand Identity', text: 'Logo, colors, and visual language' },
      { icon: 'fa-vector-square', title: 'Multi-format', text: 'Print, digital, and social assets' },
      { icon: 'fa-palette', title: 'UI Design', text: 'Web and app interface design' },
      { icon: 'fa-image', title: 'NFT Art', text: 'Generative and custom NFT artwork' },
      { icon: 'fa-film', title: 'Motion', text: 'Animated logos and explainer videos' },
      { icon: 'fa-book', title: 'Guidelines', text: 'Complete brand style guides' }
    ],
    stats: [
      { value: '89%', label: 'Recognition' },
      { value: '96%', label: 'Satisfaction' },
      { value: '48hrs', label: 'Turnaround' },
      { value: '200+', label: 'Projects' }
    ],
    tech: ['Adobe Creative Suite', 'Figma', 'Sketch', 'Blender', 'After Effects'],
    process: ['Brief', 'Concepts', 'Refinement', 'Delivery', 'Guidelines']
  },
  {
    id: 'social-media',
    category: 'marketing',
    icon: 'fa-hashtag',
    title: 'Social Media Marketing',
    tagline: 'Community & Engagement',
    badge: 'Viral Growth',
    badgeIcon: 'fa-fire',
    color: 'purple',
    image: '/assets/services/18.png',
    description: 'Build thriving communities that turn followers into advocates.',
    longDescription: 'Build and engage crypto communities across Twitter, Discord, Telegram, and beyond. We create viral content, manage communities 24/7, run ambassador programs, and drive organic growth through authentic engagement. Your community is your strongest asset.',
    features: [
      { icon: 'fa-calendar-alt', title: 'Content', text: 'Daily posts, threads, and memes' },
      { icon: 'fa-users', title: 'Community', text: 'Discord and Telegram management' },
      { icon: 'fa-chart-line', title: 'Growth', text: 'Follower and engagement growth' },
      { icon: 'fa-handshake', title: 'Partnerships', text: 'Collaborations and cross-promotions' },
      { icon: 'fa-comments', title: 'Engagement', text: 'Active community interaction' },
      { icon: 'fa-bullhorn', title: 'Announcements', text: 'Launch and update campaigns' }
    ],
    stats: [
      { value: '12.5%', label: 'Engagement' },
      { value: '280%', label: 'Growth' },
      { value: '2M+', label: 'Reach' },
      { value: '24/7', label: 'Management' }
    ],
    tech: ['Twitter', 'Discord', 'Telegram', 'Hootsuite', 'Analytics'],
    process: ['Audit', 'Strategy', 'Content Creation', 'Management', 'Growth']
  }
];

export const categories = [
  { id: 'all', label: 'All Services', icon: 'fa-th-large' },
  { id: 'blockchain', label: 'Blockchain', icon: 'fa-link', count: 13 },
  { id: 'development', label: 'Development', icon: 'fa-code', count: 2 },
  { id: 'marketing', label: 'Marketing', icon: 'fa-bullhorn', count: 3 }
];

export const getServiceById = (id) => services.find(s => s.id === id);
export const getServicesByCategory = (cat) => cat === 'all' ? services : services.filter(s => s.category === cat);

export default services;
